import React, { useState, useEffect, useRef } from 'react';
import { nanoid } from 'nanoid';
import { collection, addDoc, serverTimestamp, doc, getDoc, updateDoc, arrayUnion, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import '../../styles/HomePage.css';
import orheartImage from '../../orheart.webp';
import { searchSpiritGifs } from '../../services/firebaseApiService';
import { getRandomHelloGifsInitial } from '../../data/localGifLibraries';
import { getOrCreateConversation, sendMessage } from '../../services/messagingService';
import { getPrimaryCollagePosterNickname } from '../../utils/mainCollageNickname';
import { normalizeSavedPostersList } from '../../utils/savedPosterNormalize';
import { seedFromString } from '../../utils/seedFromString';
import { buildPosterShareUrl, mergePosterReferralIntoUserDocIfPresent } from '../../utils/posterReferralLink';
import { referFriendEmailValidationError } from '../../utils/berkeleyEmailAccess';
import { useEduProfileVerification } from '../../hooks/useEduProfileVerification';
import EduProfileVerificationPopup from '../auth/EduProfileVerificationPopup';
import { FeedNicknameTile } from '../feed/FeedNicknameTile';
import { CollageDetailsLuvLegend } from '../feed/CollageDetailsLuvLegend';
import { feedAdFindLuvIconFlags } from '../../utils/posterFindLuv';
import { matchFriendPromptBountyDollars } from '../../utils/dinnerGiveawayFeed';
import { showFriendFoxMaskPlaceholder } from '../../utils/friendCollageCutoutPlaceholder';
import FriendFoxMaskPlaceholderCutout from '../collage/FriendFoxMaskPlaceholderCutout';

/** Same collage-facing name as HomePage `feedAdCollageNickname` / MATCH A FRIEND WITH title. */
function reachOutTitleNickname(posterAd) {
  if (!posterAd) return 'Someone';
  return (String(posterAd.posterNickname ?? posterAd.userName ?? 'Someone').trim() || 'Someone');
}

/**
 * Full poster layout for shared poster links (/poster/:id).
 * Collage matches COLLAGE DETAILS / MATCH TO GET $100 DINNER layout (.match-a-friend-collage).
 */
export default function SharedPosterView({
  sharedPoster,
  onClose,
  currentUser,
  onShowSignUp,
  onOpenChat,
  onFriendSharedSaveSuccess,
}) {
  const [linkCopied, setLinkCopied] = useState(false);
  const [showReferOptions, setShowReferOptions] = useState(false);
  const eduVerification = useEduProfileVerification(currentUser);
  const [showReferEmailInput, setShowReferEmailInput] = useState(false);
  const [referFriendEmail, setReferFriendEmail] = useState('');
  const [referFriendEmailError, setReferFriendEmailError] = useState('');
  const [referFriendSubmitted, setReferFriendSubmitted] = useState(false);
  const [showReferIgInput, setShowReferIgInput] = useState(false);
  const [referFriendIg, setReferFriendIg] = useState('');
  const [referFriendIgError, setReferFriendIgError] = useState('');
  const [referFriendIgSubmitted, setReferFriendIgSubmitted] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifs, setGifs] = useState([]);
  const [gifSearch, setGifSearch] = useState('');
  const [loadingGifs, setLoadingGifs] = useState(false);
  const [selectedGif, setSelectedGif] = useState(null);
  const [gifSubmitting, setGifSubmitting] = useState(false);
  const [gifSent, setGifSent] = useState(false);
  const [saveFriendDraftStatus, setSaveFriendDraftStatus] = useState('idle'); // idle | saving | saved | error
  const sharedPosterSpiritGifWarmupRef = useRef(false);

  useEffect(() => {
    setSaveFriendDraftStatus('idle');
  }, [sharedPoster?.id]);

  useEffect(() => {
    if (!sharedPoster?.id || !currentUser?.uid) return;
    void mergePosterReferralIntoUserDocIfPresent(db, currentUser.uid, sharedPoster.id);
  }, [sharedPoster?.id, currentUser?.uid]);

  useEffect(() => {
    if (!showGifPicker) {
      sharedPosterSpiritGifWarmupRef.current = false;
      return;
    }
    if (sharedPosterSpiritGifWarmupRef.current) return;
    sharedPosterSpiritGifWarmupRef.current = true;
    const customerId = currentUser?.uid || 'anonymous';
    searchSpiritGifs('warmup', customerId).catch(() => {});
  }, [showGifPicker, currentUser?.uid]);

  if (!sharedPoster) return null;
  const ad = sharedPoster;
  const sharedLinkFindLuvFlags = feedAdFindLuvIconFlags(ad, null);
  /** Your collages → primary “Share link” copies `/poster/:id?primaryShare=1` for the shorter vibe headline. */
  const primaryCollageShareLink =
    typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('primaryShare') === '1';

  const fetchGifs = async (query = 'hello') => {
    setLoadingGifs(true);
    try {
      const customerId = currentUser?.uid || `anon_${Date.now()}`;
      const results = await searchSpiritGifs(query, customerId);
      setGifs(results.slice(0, 9));
    } catch (error) {
      console.error('Error fetching GIFs:', error);
    } finally {
      setLoadingGifs(false);
    }
  };

  const handleShareLinkClick = async () => {
    const link = buildPosterShareUrl(window.location.origin, ad.id, currentUser?.uid);
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handleReferBack = () => {
    setShowReferOptions(false);
    setShowReferEmailInput(false);
    setReferFriendEmail('');
    setReferFriendEmailError('');
    setReferFriendSubmitted(false);
    setShowReferIgInput(false);
    setReferFriendIg('');
    setReferFriendIgError('');
    setReferFriendIgSubmitted(false);
  };

  const normalizeNicknameForSave = (s) => (s || '').trim().toLowerCase();
  const getSuggestedNicknameForSave = (currentNickname, existingNicknamesList) => {
    const base = (currentNickname || '').trim().replace(/\s+\d+$/, '');
    if (!base) return 'The One 2';
    const baseNorm = base.toLowerCase();
    const escapeRe = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const numbers = [];
    for (const n of existingNicknamesList) {
      const norm = (n || '').trim().toLowerCase();
      if (norm === baseNorm) numbers.push(1);
      else {
        const m = norm.match(new RegExp('^' + escapeRe(baseNorm) + '\\s+(\\d+)$'));
        if (m) numbers.push(parseInt(m[1], 10));
      }
    }
    const maxN = numbers.length ? Math.max(...numbers) : 0;
    return `${base} ${maxN + 1}`;
  };

  const handleSaveFriendSharedDraft = async () => {
    if (!ad.friendCollageShare) return;
    if (!currentUser) {
      if (onShowSignUp) onShowSignUp('Save this collage to your drafts');
      return;
    }
    if (saveFriendDraftStatus === 'saving') return;
    setSaveFriendDraftStatus('saving');
    try {
      const nicknameBase = (ad.posterNickname ?? 'The One').trim() || 'The One';
      const userDocRef = doc(db, 'users', currentUser.uid);
      const existingSnap = await getDoc(userDocRef);
      const existingPosters = normalizeSavedPostersList(Array.isArray(existingSnap.data()?.savedWantedPosters) ? existingSnap.data().savedWantedPosters : []);
      const feedRef = collection(db, 'wantedFeed');
      const feedQuery = query(feedRef, orderBy('createdAt', 'desc'), limit(500));
      const feedSnap = await getDocs(feedQuery);
      const feedNicknames = feedSnap.docs
        .map((d) => (d.data().posterNickname ?? 'The One').trim())
        .filter(Boolean);
      const savedNicknames = (existingPosters || []).map((p) => (p.posterNickname ?? 'The One').trim()).filter(Boolean);
      const allExistingNicknamesList = [...savedNicknames, ...feedNicknames];
      const existingNicknamesSet = new Set(allExistingNicknamesList.map(normalizeNicknameForSave));
      let nicknameForPoster = nicknameBase;
      if (existingNicknamesSet.has(normalizeNicknameForSave(nicknameForPoster))) {
        nicknameForPoster = getSuggestedNicknameForSave(nicknameForPoster, allExistingNicknamesList);
      }

      const friendFoxPersist =
        typeof ad.friendFoxMaskPlaceholder === 'boolean'
          ? ad.friendFoxMaskPlaceholder
          : showFriendFoxMaskPlaceholder(ad);

      const posterData = {
        id: nanoid(),
        createdAt: new Date(),
        userId: currentUser.uid,
        surveyAnswers: ad.surveyAnswers ?? null,
        partnerCutout: ad.partnerCutout ?? null,
        heartPhotoUrl: ad.heartPhotoUrl ?? null,
        swags: Array.isArray(ad.swags) ? ad.swags : [],
        places: Array.isArray(ad.places) ? ad.places : [],
        activityX: ad.activityX ?? 'Seeking',
        activityY: ad.activityY ?? 'Match',
        activityZ: ad.activityZ ?? null,
        words: Array.isArray(ad.words) ? ad.words : [],
        userPhotoUrl: ad.userPhotoUrl ?? null,
        userPhotoOriginalUrl: ad.userPhotoOriginalUrl ?? null,
        posterNickname: nicknameForPoster,
        isPrivateCollage: false,
        postedIrl: false,
        postedTiktok: false,
        postedToFeed: false,
        feedAdId: null,
        friendLuv: !!ad.friendLuv,
        romanticLuv: !!ad.romanticLuv,
        friendCollageShare: true,
        /** Recipient saved this draft from a friend’s shared link (not “I collaged my friend” flow). */
        savedFromFriendShareLink: true,
        friendFoxMaskPlaceholder: friendFoxPersist,
        cutoutCustomizedSinceSurveyDefault: !!ad.cutoutCustomizedSinceSurveyDefault,
      };
      if (ad.selfGender && ['male', 'female', 'nb'].includes(ad.selfGender)) {
        posterData.selfGender = ad.selfGender;
      }

      await updateDoc(userDocRef, {
        savedWantedPosters: arrayUnion(posterData),
        updatedAt: new Date(),
      });
      if (onFriendSharedSaveSuccess) {
        onFriendSharedSaveSuccess();
      } else {
        setSaveFriendDraftStatus('saved');
      }
    } catch (err) {
      console.error('Save friend shared draft failed:', err);
      setSaveFriendDraftStatus('error');
      alert('Could not save to your collages. Please try again.');
    }
  };

  return (
    <div className="shared-mission-overlay shared-poster-overlay" style={{ backgroundColor: 'transparent', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}>
      <div className="shared-mission-container shared-poster-container">
        {showReferOptions ? (
          <div className="shared-poster-apply-content">
            {showReferEmailInput ? (
              <>
                <button type="button" className="feed-tile-detail-back" onClick={() => { setShowReferEmailInput(false); setReferFriendEmail(''); setReferFriendEmailError(''); setReferFriendSubmitted(false); }} aria-label="Back">‹ Back</button>
                <h2 className="feed-tile-detail-title">Enter your friend&apos;s email</h2>
                <form
                  className="feed-tile-detail-apply-form"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const email = (referFriendEmail || '').trim().toLowerCase();
                    const emailErr = referFriendEmailValidationError(email);
                    if (emailErr) {
                      setReferFriendEmailError(emailErr);
                      return;
                    }
                    setReferFriendEmailError('');
                    try {
                      await addDoc(collection(db, 'referSubmissions'), {
                        posterId: ad.id,
                        friendEmail: email,
                        createdAt: serverTimestamp(),
                        userId: currentUser?.uid || null,
                      });
                      setReferFriendSubmitted(true);
                    } catch (err) {
                      console.error('Error saving refer submission:', err);
                      setReferFriendEmailError('Something went wrong. Please try again.');
                    }
                  }}
                >
                  <input
                    type="email"
                    className="feed-tile-detail-apply-input"
                    placeholder="friend@example.com"
                    value={referFriendEmail}
                    onChange={(e) => { setReferFriendEmail(e.target.value); setReferFriendEmailError(''); }}
                    aria-label="Friend's email"
                    autoComplete="email"
                    disabled={referFriendSubmitted}
                  />
                  {referFriendEmailError && <p className="feed-tile-detail-apply-error" role="alert">{referFriendEmailError}</p>}
                  <button type="submit" className="feed-tile-detail-btn feed-tile-detail-btn-apply" disabled={referFriendSubmitted}>
                    {referFriendSubmitted ? 'Received!' : 'Send'}
                  </button>
                </form>
                {referFriendSubmitted && <p className="feed-tile-detail-thank-you">We&apos;ll send them an invite!</p>}
              </>
            ) : showReferIgInput ? (
              <>
                <button type="button" className="feed-tile-detail-back" onClick={() => { setShowReferIgInput(false); setReferFriendIg(''); setReferFriendIgError(''); setReferFriendIgSubmitted(false); }} aria-label="Back">‹ Back</button>
                <h2 className="feed-tile-detail-title">Enter their IG handle</h2>
                <form
                  className="feed-tile-detail-apply-form"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const handle = (referFriendIg || '').trim().replace(/^@/, '');
                    if (!handle) {
                      setReferFriendIgError('Please enter an IG handle.');
                      return;
                    }
                    setReferFriendIgError('');
                    try {
                      await addDoc(collection(db, 'referSubmissions'), {
                        posterId: ad.id,
                        friendIgHandle: handle,
                        createdAt: serverTimestamp(),
                        userId: currentUser?.uid || null,
                      });
                      setReferFriendIgSubmitted(true);
                    } catch (err) {
                      console.error('Error saving refer submission:', err);
                      setReferFriendIgError('Something went wrong. Please try again.');
                    }
                  }}
                >
                  <input
                    type="text"
                    className="feed-tile-detail-apply-input"
                    placeholder="@username"
                    value={referFriendIg}
                    onChange={(e) => { setReferFriendIg(e.target.value); setReferFriendIgError(''); }}
                    aria-label="Friend's IG handle"
                    autoComplete="off"
                    disabled={referFriendIgSubmitted}
                  />
                  {referFriendIgError && <p className="feed-tile-detail-apply-error" role="alert">{referFriendIgError}</p>}
                  <button type="submit" className="feed-tile-detail-btn feed-tile-detail-btn-apply" disabled={referFriendIgSubmitted}>
                    {referFriendIgSubmitted ? 'Received!' : 'Send'}
                  </button>
                </form>
                {referFriendIgSubmitted && <p className="feed-tile-detail-thank-you">We&apos;ll reach out to them!</p>}
              </>
            ) : (
              <>
                <button type="button" className="feed-tile-detail-back" onClick={handleReferBack} aria-label="Back">‹ Back</button>
                <h2 className="feed-tile-detail-title">{`MATCH A FRIEND WITH ${reachOutTitleNickname(ad)}`}</h2>
                <p className="feed-tile-detail-apply-prompt">
                  {(() => {
                    const bountyLine = matchFriendPromptBountyDollars(ad);
                    if (bountyLine) {
                      return (
                        <>
                          Match a friend - get {bountyLine}. We&apos;ll send them an invite to join.
                        </>
                      );
                    }
                    return <>We&apos;ll send them an invite to join.</>;
                  })()}
                </p>
                <div className="shared-poster-refer-options">
                  <button
                    type="button"
                    className="feed-tile-detail-btn feed-tile-detail-btn-apply"
                    onClick={() => setShowReferEmailInput(true)}
                  >
                    Enter your friend&apos;s email
                  </button>
                  <button
                    type="button"
                    className="feed-tile-detail-btn feed-tile-detail-btn-ig"
                    onClick={() => setShowReferIgInput(true)}
                  >
                    Enter their IG handle
                  </button>
                  <button
                    type="button"
                    className="feed-tile-detail-btn feed-tile-detail-btn-refer"
                    onClick={handleShareLinkClick}
                  >
                    {linkCopied ? 'Link copied!' : 'Share link'}
                  </button>
                </div>
              </>
            )}
          </div>
        ) : showGifPicker ? (
          <div className="shared-poster-apply-content">
            {gifSent ? (
              <>
                <button
                  type="button"
                  className="feed-tile-detail-back"
                  onClick={() => {
                    setShowGifPicker(false);
                    setGifSent(false);
                    setSelectedGif(null);
                    setGifSearch('');
                  }}
                  aria-label="Back"
                >
                  ‹ Back
                </button>
                <p className="feed-tile-detail-thank-you">GIF sent!</p>
              </>
            ) : selectedGif ? (
              <>
                <button
                  type="button"
                  className="feed-tile-detail-back"
                  onClick={() => setSelectedGif(null)}
                  aria-label="Back"
                >
                  ‹ Back
                </button>
                <h2 className="feed-tile-detail-title">
                  {`SAY HELLO TO ${reachOutTitleNickname(ad)}!`}
                </h2>
                <div className="reach-out-gif-preview">
                  <img
                    src={selectedGif.fullUrl || selectedGif.gifUrl}
                    alt={selectedGif.title}
                    className="reach-out-gif-preview-img"
                  />
                </div>
                <button
                  type="button"
                  className="feed-tile-detail-btn feed-tile-detail-btn-apply"
                  disabled={gifSubmitting}
                  onClick={async () => {
                    const targetUid = ad.userId;
                    if (!targetUid) {
                      console.error('Cannot reach out: poster has no userId');
                      return;
                    }
                    if (!currentUser) {
                      if (onShowSignUp) onShowSignUp();
                      return;
                    }
                    if (!(await eduVerification.ensureVerifiedProfile())) return;
                    setGifSubmitting(true);
                    try {
                      const gifUrl = selectedGif.fullUrl || selectedGif.gifUrl;
                      let senderNickname = 'Someone';
                      try {
                        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
                        const ud = userSnap.data();
                        const posters = normalizeSavedPostersList(Array.isArray(ud?.savedWantedPosters) ? ud.savedWantedPosters : []);
                        const fromMain = getPrimaryCollagePosterNickname(posters, ud?.primaryCollageId ?? null);
                        const latest = posters[posters.length - 1];
                        senderNickname = fromMain
                          || latest?.posterNickname
                          || (currentUser?.displayName && String(currentUser.displayName).trim())
                          || 'Someone';
                      } catch (_) {
                        senderNickname =
                          (currentUser?.displayName && String(currentUser.displayName).trim()) || 'Someone';
                      }
                      const targetNickname = ad.posterNickname || 'Someone';
                      const conversationId = await getOrCreateConversation(
                        currentUser,
                        targetUid,
                        { senderDisplayName: senderNickname, targetDisplayName: targetNickname },
                      );
                      await sendMessage(conversationId, currentUser.uid, '', { gifUrl });
                      if (onOpenChat) {
                        onOpenChat(conversationId);
                      } else {
                        setGifSent(true);
                      }
                    } catch (err) {
                      console.error('Error sending reach-out GIF:', err);
                    } finally {
                      setGifSubmitting(false);
                    }
                  }}
                >
                  {gifSubmitting ? 'Sending…' : 'SEND'}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="feed-tile-detail-back"
                  onClick={() => {
                    setShowGifPicker(false);
                    setGifSearch('');
                  }}
                  aria-label="Back"
                >
                  ‹ Back
                </button>
                <h2 className="feed-tile-detail-title">
                  {`SAY HELLO TO ${reachOutTitleNickname(ad)}!`}
                </h2>
                <p className="feed-tile-detail-apply-prompt">
                  Pick a GIF to introduce yourself
                </p>
                <div className="heart-gif-picker">
                  <div className="heart-gif-grid">
                    {(loadingGifs
                      ? (gifs.length > 0
                          ? gifs.map(g => ({ ...g, placeholder: true }))
                          : Array.from({ length: 9 }, (_, i) => ({ id: `ph-${i}`, placeholder: true })))
                      : gifs
                    ).map((gif) => {
                      if (gif.placeholder) {
                        return <div key={gif.id} className="heart-gif-tile heart-gif-placeholder" />;
                      }
                      return (
                        <button
                          key={gif.id}
                          className="heart-gif-tile"
                          onClick={() => setSelectedGif(gif)}
                        >
                          <span className="heart-gif-spinner" />
                          <img
                            src={gif.stillUrl || gif.gifUrl}
                            alt={gif.title}
                            className="heart-gif-tile-img"
                            loading="lazy"
                            onLoad={(e) => {
                              e.target.parentElement.querySelector('.heart-gif-spinner')?.classList.add('hidden');
                              if (gif.stillUrl && e.target.src !== gif.gifUrl) {
                                e.target.src = gif.gifUrl;
                              }
                            }}
                          />
                        </button>
                      );
                    })}
                  </div>
                  <div className="heart-gif-search-wrap">
                    <input
                      type="text"
                      className="heart-gif-search-input"
                      placeholder="Search KLIPY GIFs..."
                      value={gifSearch}
                      onChange={(e) => setGifSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && gifSearch.trim()) fetchGifs(gifSearch.trim());
                      }}
                    />
                    <button
                      className="heart-gif-search-btn"
                      onClick={() => { if (gifSearch.trim()) fetchGifs(gifSearch.trim()); }}
                      disabled={!gifSearch.trim() || loadingGifs}
                    >
                      SEARCH
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
        <div className="wanted-poster wanted-poster-feed shared-poster-view">
          <p className="shared-poster-vibe-title">
            {ad.friendCollageShare
              ? 'Your friend collaged you!'
              : primaryCollageShareLink
                ? "Your friend thinks you two would vibe!"
                : "Your friend thinks you two would vibe. If it's mutual, get a paid-for date on us!"}
          </p>
          <CollageDetailsLuvLegend
            {...sharedLinkFindLuvFlags}
            lookingForGenders={ad?.lookingForGenders}
            auraColor={ad?.auraColor}
          />
          <div className="poster-content">
            <FeedNicknameTile
              ad={ad}
              savedPosters={[]}
              currentUserId={currentUser?.uid ?? null}
              findLuvFlags={sharedLinkFindLuvFlags}
              bountyAmount={ad.bountyAmount}
              showVibeAura
            />
            <div className="match-a-friend-collage">
            <div className="collage-poster-area">
              <div className="partner-swag-container">
                <div className="partner-swag-left">
                  {ad.swags && ad.swags.length > 0 && (
                    <div className="swag-images-left">
                      {ad.swags.slice(0, 3).map((swag, swagIndex) => {
                        const swagUrl = swag.imageUrl || swag;
                        if (!swagUrl) return null;
                        const seed = seedFromString(`${ad.id}-swag-${swagIndex}`);
                        const seedX = seedFromString(`${ad.id}-swag-x-${swagIndex}`);
                        const rotation = (seed * 30 - 15).toFixed(1);
                        const horizontalOffset = (seedX * 40 - 20).toFixed(1);
                        return (
                          <img
                            key={swag.id || swagIndex}
                            src={swagUrl}
                            alt={swag.name || 'Brand'}
                            className="swag-image"
                            style={{
                              transform: `rotate(${rotation}deg)`,
                              marginLeft: `${horizontalOffset}px`
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="partner-swag-center">
                  <div className="partner-swag-center-stack">
                    {(() => {
                      const cutJagged = (() => {
                        const v = 1 + Math.floor(seedFromString(`feed-cutout-${ad.id}`) * 3);
                        return v === 1 ? '' : ` partner-cutout-jagged-${v}`;
                      })();
                      if (showFriendFoxMaskPlaceholder(ad) && ad.partnerCutout) {
                        return (
                          <FriendFoxMaskPlaceholderCutout
                            variant="feed"
                            jaggedClassName={cutJagged}
                            heartSrc={ad.heartPhotoUrl || ad.surveyAnswers?.vibe_photo || orheartImage}
                            heartClassName="partner-cutout-gif-overlay"
                          />
                        );
                      }
                      if (ad.partnerCutout) {
                        return (
                          <div className="final-partner-image-wrapper">
                            <img
                              src={ad.partnerCutout}
                              alt=""
                              className={`final-partner-image final-partner-image-feed${cutJagged}`}
                            />
                            <img
                              src={ad.heartPhotoUrl || ad.surveyAnswers?.vibe_photo || orheartImage}
                              alt=""
                              className="partner-cutout-gif-overlay"
                            />
                          </div>
                        );
                      }
                      return (
                        <div
                          className={`final-partner-image final-partner-image-placeholder final-partner-image-feed${cutJagged}`}
                        >
                          👤
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <div className="partner-swag-right">
                  <div className="activities-emojis-right">
                    {[ad.activityX, ad.activityY, ad.activityZ].filter(Boolean).map((label, i) => {
                      const seed = seedFromString(`${ad.id}-act-${i}`);
                      const seedX = seedFromString(`${ad.id}-act-x-${i}`);
                      const rotation = (seed * 30 - 15).toFixed(1);
                      const horizontalOffset = (seedX * 40 - 20).toFixed(1);
                      const hue = 32 + seed * 20;
                      const bgColor = `hsl(${hue.toFixed(0)}, 90%, 72%)`;
                      const displayLabel = typeof label === 'string' ? label : (label?.emoji ?? label?.name ?? '');
                      return (
                        <div
                          key={i}
                          className="activity-tile-outer"
                          style={{
                            transform: `rotate(${rotation}deg)`,
                            marginLeft: `${horizontalOffset}px`
                          }}
                        >
                          <div
                            className="activity-emoji-wrapper collage-activity-tile"
                            style={{ background: bgColor }}
                          >
                            <div
                              className="activity-emoji"
                              style={{ fontSize: `${(2.5 + seed * 1.5).toFixed(1)}rem` }}
                            >
                              {displayLabel}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              {ad.places && ad.places.length > 0 && (
                <div className="collage-aspirations-row">
                  {[ad.places.slice(0, Math.floor(seedFromString(`${ad.id}-places-split`) * 2) + 1), ad.places.slice(Math.floor(seedFromString(`${ad.id}-places-split`) * 2) + 1)].map((rowPlaces, rowIdx) => (
                    rowPlaces.length > 0 && (
                      <div key={rowIdx} className="collage-places-row-inner">
                        {rowPlaces.map((place, i) => (
                          <div key={place.id || `${rowIdx}-${i}`} className="aspiration-tile-outer">
                            <div className="collage-place-tile">
                              <img src={place.imageUrl} alt={place.name} className="collage-place-img" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  ))}
                </div>
              )}
            </div>
            </div>
          </div>
          <div className="feed-tile-detail-actions shared-poster-actions">
            {ad.friendCollageShare ? (
              <button
                type="button"
                className="feed-tile-detail-btn feed-tile-detail-btn-apply"
                disabled={saveFriendDraftStatus === 'saving' || saveFriendDraftStatus === 'saved'}
                onClick={handleSaveFriendSharedDraft}
              >
                {saveFriendDraftStatus === 'saving' ? 'Saving…' : saveFriendDraftStatus === 'saved' ? 'Saved' : 'SAVE'}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="feed-tile-detail-btn feed-tile-detail-btn-apply"
                  onClick={async () => {
                    if (currentUser && !(await eduVerification.ensureVerifiedProfile())) return;
                    setShowGifPicker(true);
                    setGifs(getRandomHelloGifsInitial());
                  }}
                >
                  UNMASK
                </button>
                <button
                  type="button"
                  className="feed-tile-detail-btn feed-tile-detail-btn-refer"
                  onClick={() => setShowReferOptions(true)}
                >
                  SHARE
                </button>
              </>
            )}
            <div className="shared-poster-close-block">
              <p className="shared-poster-close-hint">
                masked for privacy - unmask if you vibe
              </p>
              <button
                type="button"
                className="shared-poster-close-inline"
                onClick={onClose}
                aria-label="Learn more"
              >
                learn more
                <span className="earn-foxes-dinner-intro-btn-inline-end">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="earn-foxes-matcha-arrow earn-foxes-dinner-intro-btn-arrow" aria-hidden><path d="M7 17 17 7M7 7h10v10" /></svg>
                </span>
              </button>
            </div>
          </div>
        </div>
        )}
      </div>
      <EduProfileVerificationPopup
        open={eduVerification.showPopup}
        onClose={eduVerification.closePopup}
        pending={eduVerification.pending}
        submitted={eduVerification.submitted}
        emailConfirmed={eduVerification.emailConfirmed}
        sendFailed={eduVerification.sendFailed}
        submittedEmail={eduVerification.submittedEmail}
        emailInput={eduVerification.emailInput}
        onEmailInputChange={(value) => {
          eduVerification.setEmailInput(value);
          eduVerification.setEmailError('');
        }}
        emailError={eduVerification.emailError}
        submitting={eduVerification.submitting}
        onSubmit={eduVerification.submitEduEmail}
        onResend={eduVerification.resendEduEmail}
        resendCooldownSeconds={eduVerification.resendCooldownSeconds}
      />
    </div>
  );
}
